import React, { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import {
  GENERIC_PAYMENT_ERROR,
  PAYMENT_METHODS,
  PAYMENT_PROCESSOR,
  PAYMENT_PROVIDER,
} from '../../constants';
import { payCheck } from '../../../../store/slices/payment/paymentThunk';
import Check from '../check/check';
import StripeElement from '../../stripe/StripeElement';
import { buildPaymentApi } from '../../../../api';
import { resolvePaymentErrorMessage } from '../../../../api/payment/paymentErrors';
import { XpressBillPay } from '../../xpressbillpay';
import { SnapPay } from '../../snappay';
import { CallbackResult, RedirectBasedPayment } from './RedirectBasedPayment';
import {
  PaymentApiResponse,
  PaymentFees,
  PaymentMethod,
} from 'src/types/payment';
import { getPaymentCategory } from '../../../../common/paymentUtils';
import { FLOWS_ENUM } from 'src/common/enums';
import { useAppDispatch } from '../../../../store/store';
import { selectPciPaymentApiEnabled } from '../../../../store/slices/config/configSelectors';
import { selectCurrentIDToken } from '../../../../store/slices/auth/authSelectors';
import { selectPermitApplicationRegistrant } from '../../../../store/slices/apply/applySelectors';
import { selectPermitRegistrant } from '../../../../store/slices/renew/renewSelectors';
import { selectCurrentUser } from '../../../../store/slices/auth/authSelectors';
import {
  selectCalculatedTaxData,
  selectTaxFlowData,
} from '../../../../store/slices/tax/taxSelectors';
import { calculateTotalDue, isGeneric } from '../utils';
import { handlePostRedirect } from './customHandlers';
import { buildGenericProviderPayload } from './payloadBuilders';

export interface PaymentFormProps {
  fees: PaymentFees;
  flow: FLOWS_ENUM;
  onSuccess: (isGeneric?: boolean) => void;
  paymentID: string;
  paymentMethod: PaymentMethod;
  paymentProvider?: PAYMENT_PROVIDER;
  permitIdentifier: string;
  processor: PAYMENT_PROCESSOR;
  signatoryName: string;
  userEmail: string;
}

export const PaymentForm = ({
  fees,
  flow,
  onSuccess,
  paymentID,
  paymentMethod,
  paymentProvider,
  permitIdentifier,
  processor,
  signatoryName,
  userEmail,
}: PaymentFormProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const dispatch = useAppDispatch();
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const authToken = useSelector(selectCurrentIDToken);
  const applyRegistrant = useSelector(selectPermitApplicationRegistrant);
  const renewRegistrant = useSelector(selectPermitRegistrant);
  const currentUser = useSelector(selectCurrentUser);
  const taxFlowData = useSelector(selectTaxFlowData);
  const calculatedTaxData = useSelector(selectCalculatedTaxData);
  const apiAccess = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);

  const person = useMemo(() => {
    if (flow === FLOWS_ENUM.APPLY_LICENSE) return applyRegistrant?.registrant;
    if (flow === FLOWS_ENUM.RENEW_LICENSE || flow === FLOWS_ENUM.BULK_RENEWAL)
      return renewRegistrant?.registrant;
    if (flow === FLOWS_ENUM.PAY_TOT && currentUser)
      return {
        firstName: currentUser.firstName ?? '',
        lastName: currentUser.lastName ?? '',
        email: currentUser.email,
        mailingAddress: currentUser.mailingAddress ?? '',
        phone: currentUser.phone ?? '',
        companyName: currentUser.companyName ?? '',
      };
    return undefined;
  }, [flow, applyRegistrant, renewRegistrant, currentUser]);

  const selectedLicenses = useMemo(
    () => taxFlowData?.selectedLicenses ?? [],
    [taxFlowData]
  );

  const isGenericProvider = useMemo(() => isGeneric(processor), [processor]);

  const getPaymentApi = (
    paymentMethod: PaymentMethod,
    processor: PAYMENT_PROCESSOR
  ) => {
    switch (paymentMethod) {
      case PAYMENT_METHODS.CHECK:
        return async () => dispatch(payCheck());
      case PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD:
        if (processor === PAYMENT_PROCESSOR.STRIPE) {
          return () =>
            apiAccess.payByCreditCard(
              {
                email: userEmail,
                payment_category: getPaymentCategory(flow as FLOWS_ENUM)!,
                payment_id: Number(paymentID),
                signatory: signatoryName,
                subtotal: fees.summary?.total ?? 0,
                signature: fees.signature ?? '',
                credits: fees.summary?.credit ?? 0,
              },
              authToken
            );
        }
        return () =>
          apiAccess.payGeneric(
            {
              email: userEmail,
              payment_category: getPaymentCategory(flow)!,
              payment_id: Number(paymentID),
              signatory: signatoryName,
              subtotal: totalDue,
              signature: fees.signature ?? '',
              credits: fees.summary?.credit ?? 0,
              payload: buildGenericProviderPayload(
                paymentProvider,
                person,
                selectedLicenses,
                flow,
                permitIdentifier,
                totalDue,
                paymentMethod,
                calculatedTaxData
              ),
            },
            authToken
          );
      case PAYMENT_METHODS.ACH_DIRECT_DEBIT:
        if (processor === PAYMENT_PROCESSOR.STRIPE) {
          return () =>
            apiAccess.payByACHDirectDebit(
              {
                email: userEmail,
                payment_category: getPaymentCategory(flow)!,
                payment_id: Number(paymentID),
                signatory: signatoryName,
                subtotal: fees.summary?.total ?? 0,
                signature: fees.signature ?? '',
                credits: fees.summary?.credit ?? 0,
              },
              authToken
            );
        }
        return () =>
          apiAccess.payGeneric(
            {
              email: userEmail,
              payment_category: getPaymentCategory(flow)!,
              payment_id: Number(paymentID),
              signatory: signatoryName,
              subtotal: totalDue,
              signature: fees.signature ?? '',
              credits: fees.summary?.credit ?? 0,
              payload: buildGenericProviderPayload(
                paymentProvider,
                person,
                selectedLicenses,
                flow,
                permitIdentifier,
                totalDue,
                paymentMethod,
                calculatedTaxData
              ),
            },
            authToken
          );
      default:
        return undefined;
    }
  };

  const totalDue = useMemo(
    () => calculateTotalDue(paymentMethod, fees, isGenericProvider),
    [fees, paymentMethod, isGenericProvider]
  );

  const handlePayment = async (): Promise<PaymentApiResponse | void> => {
    if (
      processor === PAYMENT_PROCESSOR.STRIPE &&
      pciEnabled &&
      paymentMethod !== PAYMENT_METHODS.CHECK
    ) {
      onSuccess(false);
      return;
    }
    setLoading(true);
    const paymentApi = getPaymentApi(paymentMethod, processor);
    try {
      if (paymentApi) {
        const response = await paymentApi();
        onSuccess(
          (isGenericProvider && paymentMethod !== PAYMENT_METHODS.CHECK) ||
            (pciEnabled && paymentMethod === PAYMENT_METHODS.CHECK)
        );
        return response;
      }
      setError(GENERIC_PAYMENT_ERROR);
      setLoading(false);
    } catch (err) {
      setError(resolvePaymentErrorMessage(err, GENERIC_PAYMENT_ERROR));
      Sentry.captureException(err);
      setLoading(false);
    }
  };

  const handleGenericPayment = async (): Promise<CallbackResult> => {
    const response = await handlePayment();
    if (response && 'url' in response) {
      if (response.checkout_mode === 'post_redirect') {
        handlePostRedirect(response, paymentProvider);
      } else if (response.url?.startsWith('http')) {
        window.open(response.url, '_blank', 'toolbar=0,location=0,menubar=0');
      }
      return response;
    }
    return { url: '' };
  };

  if (paymentMethod === PAYMENT_METHODS.CHECK) {
    return (
      <Check
        onPay={handlePayment}
        loading={loading}
        error={error}
      />
    );
  } else if (processor === PAYMENT_PROCESSOR.STRIPE) {
    return (
      <StripeElement
        flow={flow}
        onPay={handlePayment}
        paymentMethod={paymentMethod}
      />
    );
  } else if (paymentProvider === PAYMENT_PROVIDER.XPRESS_BILL_PAY) {
    return (
      <XpressBillPay
        onPay={handlePayment}
        permitIdentifier={permitIdentifier}
        totalDue={totalDue}
        loading={loading}
        error={error}
      />
    );
  } else if (paymentProvider === PAYMENT_PROVIDER.SNAPPAY) {
    return (
      <SnapPay
        flow={flow}
        paymentID={paymentID}
        paymentMethod={paymentMethod}
        signature={fees.signature ?? ''}
        signatoryName={signatoryName}
        userEmail={userEmail}
        totalDue={totalDue}
        credits={fees.summary?.credit ?? 0}
        onSuccess={onSuccess}
      />
    );
  } else if (isGenericProvider) {
    return (
      <RedirectBasedPayment
        dataTestId="generic-form-component"
        error={error}
        loading={loading}
        onClickCallback={handleGenericPayment}
        totalDue={totalDue}
      >
        The button below will take you to our third-party payment system to make
        your payment.
      </RedirectBasedPayment>
    );
  }

  return <></>;
};
